import React from 'react';
import { cn } from '../utils/cn';

export type StatusKind =
  | 'pending'
  | 'progress'
  | 'success'
  | 'error'
  | 'info'
  | 'warning'
  | 'neutral';

export interface StatusPillProps extends React.HTMLAttributes<HTMLSpanElement> {
  kind?: StatusKind;
  /** Show a leading dot indicator */
  showDot?: boolean;
  /** Show a pulsing animation on the dot (e.g. for active/in-progress states) */
  pulse?: boolean;
  size?: 'sm' | 'md';
}

const kindMap: Record<StatusKind, { bg: string; dot: string }> = {
  pending: {
    bg: 'bg-warning-container text-warning-container-foreground border border-warning/30',
    dot: 'bg-warning',
  },
  progress: {
    bg: 'bg-info-container text-info-container-foreground border border-info/30',
    dot: 'bg-info',
  },
  success: {
    bg: 'bg-success-container text-success-container-foreground border border-success/30',
    dot: 'bg-success',
  },
  error: {
    bg: 'bg-error-container text-error-container-foreground border border-destructive/30',
    dot: 'bg-destructive',
  },
  info: {
    bg: 'bg-info-container text-info-container-foreground border border-info/30',
    dot: 'bg-info',
  },
  warning: {
    bg: 'bg-warning-container text-warning-container-foreground border border-warning/30',
    dot: 'bg-warning',
  },
  neutral: {
    bg: 'bg-surface-variant text-surface-variant-foreground border border-outline/40',
    dot: 'bg-outline',
  },
};

const sizeMap = {
  sm: 'h-5 px-2 text-label-sm gap-1',
  md: 'h-6 px-2.5 text-label-sm gap-1.5',
};

export const StatusPill: React.FC<StatusPillProps> = ({
  kind = 'neutral',
  showDot = true,
  pulse = false,
  size = 'md',
  className,
  children,
  ...props
}) => {
  const { bg, dot } = kindMap[kind];
  return (
    <span
      className={cn('inline-flex items-center justify-center rounded-full font-medium whitespace-nowrap', sizeMap[size], bg, className)}
      {...props}
    >
      {showDot ? (
        <span className="relative inline-flex h-2 w-2 shrink-0">
          {pulse ? (
            <span className={cn('absolute inset-0 inline-flex animate-ping rounded-full opacity-60', dot)} aria-hidden />
          ) : null}
          <span className={cn('relative inline-flex h-2 w-2 rounded-full', dot)} aria-hidden />
        </span>
      ) : null}
      <span>{children}</span>
    </span>
  );
};
